import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import Image from 'next/image';
import type { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useUser from '@alltrails/context/hooks/useUser';
import { BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import type { SubscriptionFeatures } from '@alltrails/types/SubscriptionFeatures';
import type { Upsell } from '@alltrails/types/Upsell';
import BaseUpsellModal from '../BaseUpsell/BaseUpsellModal';
import GenericUpsellContent from '../GenericUpsellContent/GenericUpsellContent';
import getServerUpsellCopy from '../getServerUpsellCopy';
import getUpsellCtaText from '../getUpsellCtaText';

type Props = {
  headerImageSrc: StaticImport;
  heading: ReactNode;
  isOpen: boolean;
  onCloseRequest: ComponentProps<typeof BaseUpsellModal>['onCloseRequest'];
  promoEligibleHeading: ReactNode;
  upsellTrigger: UpsellTrigger;
  ctaText?: ReactNode;
  isCrossPlatform?: ComponentProps<typeof BaseUpsellModal>['isCrossPlatform'];
  isFocusPaused?: boolean;
  onCrossPlatformRequest?: ComponentProps<typeof BaseUpsellModal>['onCrossPlatformRequest'];
  onSuccess?: ComponentProps<typeof BaseUpsellModal>['onSuccess'];
  promoEligibleCtaText?: ReactNode;
  promoEligibleSubCopy?: ReactNode;
  promoEligibleSubheading?: ReactNode;
  serverUpsellContent?: Upsell;
  skipCheckout?: boolean;
  subCopy?: ReactNode;
  subheading?: ReactNode;
  subscriptionFeatures?: SubscriptionFeatures;
  trailId?: ComponentProps<typeof BaseUpsellModal>['trailId'];
};

// https://www.figma.com/design/5lZfiEveGScwx75JKpB12W/Upsell-XPs-2026?node-id=4723-14734&m=dev
export default function PlusUpsell2026({
  ctaText,
  headerImageSrc,
  heading,
  isCrossPlatform,
  isFocusPaused,
  isOpen,
  onCloseRequest,
  onCrossPlatformRequest,
  onSuccess,
  promoEligibleCtaText,
  promoEligibleHeading,
  promoEligibleSubCopy,
  promoEligibleSubheading,
  serverUpsellContent,
  skipCheckout,
  subCopy,
  subheading,
  subscriptionFeatures,
  trailId,
  upsellTrigger
}: Props) {
  const user = useUser();
  const isPromoEligible = !user || user.isPromoEligible;

  const serverCopy = getServerUpsellCopy({ isPromoEligible, serverUpsellContent });

  const resolvedSubCopy = serverCopy.subCopy || (isPromoEligible ? promoEligibleSubCopy : subCopy);
  const resolvedCtaText = serverCopy.ctaText || (isPromoEligible ? promoEligibleCtaText : ctaText);

  return (
    <BaseUpsellModal
      caption={resolvedSubCopy}
      headerImage={<Image alt="" sizes={`(max-width: ${BREAKPOINT_SM_SIZE}px) 100vw, 480px`} src={headerImageSrc} />}
      headerImageV3
      heading={serverCopy.heading || heading}
      isCrossPlatform={isCrossPlatform}
      isFocusPaused={isFocusPaused}
      isOpen={isOpen}
      onCloseRequest={onCloseRequest}
      onCrossPlatformRequest={onCrossPlatformRequest}
      onSuccess={onSuccess}
      primaryButtonText={resolvedCtaText ?? getUpsellCtaText({ tier: 'plus', user })}
      primaryButtonVariant="accent"
      productsAvailable="plus"
      promoEligibleHeading={serverCopy.promoEligibleHeading || promoEligibleHeading}
      purchaseTier="plus"
      skipCheckout={skipCheckout}
      tallerOnMobile
      trailId={trailId}
      upsellTrigger={upsellTrigger}
    >
      <GenericUpsellContent
        featureListLabel={<FormattedMessage defaultMessage="Plus also includes" />}
        heading={serverCopy.heading || heading}
        isPromoEligible={isPromoEligible}
        promoEligibleHeading={serverCopy.promoEligibleHeading || promoEligibleHeading}
        promoEligibleSubheading={serverCopy.promoEligibleSubheading || promoEligibleSubheading}
        subheading={serverCopy.subheading || subheading}
        subscriptionFeatures={serverCopy.subscriptionFeatures || subscriptionFeatures}
        tier="plus"
        upsellTrigger={upsellTrigger}
      />
    </BaseUpsellModal>
  );
}
